import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateClientMessage } from '../../hooks/useMessaging';
import { useClients } from '../../hooks/useClients';
import { useAttorney } from '../../context/AttorneyContext';
import './MessageComposeModal.css';

/**
 * Message Compose Modal
 * For MarkoTM attorneys to compose messages to clients
 *
 * External attorneys should not see this modal - they use RequestViaMarkoTMModal instead.
 */
function MessageComposeModal({ isOpen, onClose, applicationId: initialAppId, clientName: initialClientName, clientEmail }) {
  const { t } = useTranslation('attorney');
  const { canMessageClients, attorneyName } = useAttorney();
  const createMessage = useCreateClientMessage();
  const { data: clientsData, isLoading: isLoadingClients } = useClients();

  const [searchQuery, setSearchQuery] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(!initialAppId);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const [formData, setFormData] = useState({
    applicationId: initialAppId || '',
    type: 'info_request',
    subject: '',
    body: '',
    urgency: 'medium'
  });

  // Access check
  if (!canMessageClients) {
    return null;
  }

  // Filter clients based on search query
  const filteredClients = useMemo(() => {
    if (!clientsData?.clients) return [];
    if (!searchQuery.trim()) return clientsData.clients;

    const query = searchQuery.toLowerCase();
    return clientsData.clients.filter(client => {
      // Search by client name or email
      if (client.name?.toLowerCase().includes(query)) return true;
      if (client.email?.toLowerCase().includes(query)) return true;

      // Search by application ID or trademark name
      return client.applications?.some(app =>
        app.applicationId?.toLowerCase().includes(query) ||
        app.trademarkName?.toLowerCase().includes(query)
      );
    });
  }, [clientsData?.clients, searchQuery]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectApplication = (client, application) => {
    setSelectedClient(client);
    setSelectedApplication(application);
    setFormData(prev => ({
      ...prev,
      applicationId: application.applicationId || application.id
    }));
    setShowClientPicker(false);
  };

  const handleClearSelection = () => {
    setSelectedClient(null);
    setSelectedApplication(null);
    setFormData(prev => ({
      ...prev,
      applicationId: ''
    }));
    setShowClientPicker(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.applicationId || !formData.subject || !formData.body) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await createMessage.mutateAsync({
        applicationId: formData.applicationId,
        messageData: {
          type: formData.type,
          subject: formData.subject,
          body: formData.body,
          urgency: formData.urgency,
          responseRequired: formData.type !== 'general',
          senderName: attorneyName
        }
      });

      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
      alert(`Error sending message: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  // Display name for the recipient - clean up trademark name
  const displayClientName = selectedClient?.name || initialClientName;
  const displayClientEmail = selectedClient?.email || clientEmail;

  // Get a cleaner trademark name (remove file extensions, etc.)
  let displayTrademarkName = selectedApplication?.trademarkName || '';
  // Remove common file extensions from trademark name display
  displayTrademarkName = displayTrademarkName.replace(/[-_](image|logo|tm)?\d*\.(png|jpg|jpeg|gif|svg|webp)$/i, '');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content message-compose-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('attorney.compose.title', 'Message Client')}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Client/Application Picker */}
            {!initialAppId && showClientPicker ? (
              <div className="client-picker-section">
                <label>{t('attorney.compose.selectClient', 'Select Client & Application')} *</label>

                <div className="client-search">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('attorney.compose.searchPlaceholder', 'Search by client name, email, or trademark...')}
                    autoFocus
                  />
                </div>

                <div className="client-list">
                  {isLoadingClients ? (
                    <div className="client-list-loading">
                      {t('common.loading', 'Loading...')}
                    </div>
                  ) : filteredClients.length === 0 ? (
                    <div className="client-list-empty">
                      {searchQuery
                        ? t('attorney.compose.noResultsFound', 'No clients or applications found')
                        : t('attorney.compose.noClients', 'No clients available')
                      }
                    </div>
                  ) : (
                    filteredClients.map(client => (
                      <div key={client.userId} className="client-item">
                        <div className="client-header">
                          <span className="client-name">{client.name || 'Unknown'}</span>
                          {client.email && (
                            <span className="client-email">{client.email}</span>
                          )}
                          <span className="client-app-count">
                            {client.applications?.length || 0} {t('attorney.compose.applications', 'applications')}
                          </span>
                        </div>

                        {client.applications && client.applications.length > 0 && (
                          <div className="client-applications">
                            {client.applications.map(app => {
                              // Clean up trademark name for display
                              let cleanName = app.trademarkName || 'Untitled';
                              cleanName = cleanName.replace(/[-_](image|logo|tm)?\d*\.(png|jpg|jpeg|gif|svg|webp)$/i, '');

                              return (
                                <button
                                  key={app.id}
                                  type="button"
                                  className="application-item"
                                  onClick={() => handleSelectApplication(client, app)}
                                >
                                  <span className="app-trademark">{cleanName}</span>
                                  <span className={`app-status status-${app.status}`}>
                                    {app.status}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              /* Selected Application Display */
              <div className="selected-recipient">
                <div className="recipient-info">
                  <div className="recipient-row">
                    <label>{t('attorney.compose.to', 'To')}:</label>
                    <div className="recipient-details">
                      <span className="recipient-name">{displayClientName || 'Client'}</span>
                      {displayClientEmail && (
                        <span className="recipient-email">{displayClientEmail}</span>
                      )}
                    </div>
                  </div>
                  {displayTrademarkName && (
                    <div className="recipient-row">
                      <label>{t('attorney.compose.regarding', 'Regarding')}:</label>
                      <span className="recipient-trademark">{displayTrademarkName}</span>
                    </div>
                  )}
                </div>
                {!initialAppId && (
                  <button
                    type="button"
                    className="btn-change-recipient"
                    onClick={handleClearSelection}
                  >
                    {t('attorney.compose.change', 'Change')}
                  </button>
                )}
              </div>
            )}

            {/* Rest of form - only show when recipient is selected */}
            {(formData.applicationId || initialAppId) && (
              <>
                <div className="form-group">
                  <label htmlFor="type">{t('attorney.compose.messageType', 'Message Type')}</label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                  >
                    <option value="info_request">
                      {t('attorney.compose.infoRequest', 'Request Information')}
                    </option>
                    <option value="general">
                      {t('attorney.compose.generalUpdate', 'General Update')}
                    </option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">{t('attorney.compose.subject', 'Subject')} *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="e.g., Information needed for your trademark filing"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="body">{t('attorney.compose.message', 'Message')} *</label>
                  <textarea
                    id="body"
                    name="body"
                    value={formData.body}
                    onChange={handleChange}
                    rows={8}
                    placeholder="Enter your message to the client..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="urgency">{t('attorney.compose.urgency', 'Priority')}</label>
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                  >
                    <option value="low">{t('attorney.compose.urgencyLow', 'Low')}</option>
                    <option value="medium">{t('attorney.compose.urgencyMedium', 'Normal')}</option>
                    <option value="high">{t('attorney.compose.urgencyHigh', 'High')}</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMessage.isPending || !formData.applicationId}
            >
              {createMessage.isPending
                ? t('common.sending', 'Sending...')
                : t('attorney.compose.send', 'Send Message')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MessageComposeModal;
